import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import CommonDataTable from '@libComponents/table/CommonDataTable';
import { theme } from 'app/Theme';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import {
    CLIENT_TYPE_FILTER_OPTIONS,
    ACTIVE_STATUS_FILTER_OPTIONS,
    ROWS_PER_PAGE_OPTIONS,
    TABLE_COLUMNS
} from '../common';
import ClientTableBody from './ClientTableBody';
import ClientDialog from './ClientDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

const ROWS_PER_PAGE_DATA = ROWS_PER_PAGE_OPTIONS.map((option) => ({
    label: `${option}`,
    value: option
}));

const ClientManagement = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const clients = useSelector(selectors.clientsSelector);
    const pagination = useSelector(selectors.paginationSelector);
    const filters = useSelector(selectors.filtersSelector);
    const sort = useSelector(selectors.sortSelector);
    const lastCreatedClientId = useSelector(selectors.lastCreatedClientIdSelector);
    const teamOptions = useSelector(selectors.teamOptionsSelector);
    const employeeOptions = useSelector(selectors.employeeOptionsSelector);

    const [searchInput, setSearchInput] = useState('');
    const initialUrlSynced = useRef(false);

    // Sync URL search params to Redux filters on mount
    useEffect(() => {
        if (!initialUrlSynced.current) {
            initialUrlSynced.current = true;
            const search = searchParams.get('search') || '';
            const type = searchParams.get('type') || '';
            const isActive = searchParams.get('isActive') ?? '';
            const employeeEmail = searchParams.get('employeeEmail') || '';
            const teamId = searchParams.get('teamId') || '';
            const offset = parseInt(searchParams.get('page') || '1', 10);
            const limit = parseInt(searchParams.get('limit') || '10', 10);
            const sortBy = searchParams.get('sortBy') || 'updatedAt';
            const sortDescending = searchParams.get('sortDescending') !== 'false';

            dispatch(actions.setFilters({ search, type, isActive, employeeEmail, teamId }));
            dispatch(actions.setPagination({ offset, limit }));
            dispatch(actions.setSort({ sortBy, sortDescending }));
            setSearchInput(search);
        }
    }, [searchParams, dispatch]);

    // Sync Redux state changes to URL search params
    useEffect(() => {
        if (!initialUrlSynced.current) return;
        const params = new URLSearchParams();
        if (filters.search) params.set('search', filters.search);
        if (filters.type) params.set('type', filters.type);
        if (filters.isActive !== '' && filters.isActive !== undefined && filters.isActive !== null) {
            params.set('isActive', String(filters.isActive));
        }
        if (filters.employeeEmail) params.set('employeeEmail', filters.employeeEmail);
        if (filters.teamId) params.set('teamId', filters.teamId);
        if (pagination.offset && pagination.offset > 1) params.set('page', String(pagination.offset));
        if (pagination.limit && pagination.limit !== 10) params.set('limit', String(pagination.limit));
        if (sort.sortBy) params.set('sortBy', sort.sortBy);
        if (sort.sortDescending !== undefined) params.set('sortDescending', String(sort.sortDescending));
        setSearchParams(params, { replace: true });
    }, [filters, pagination.offset, pagination.limit, sort, setSearchParams]);

    useEffect(() => { setSearchInput(filters.search); }, [filters.search]);

    useEffect(() => {
        dispatch(actions.fetchClients());
        dispatch(actions.fetchTeamOptions());
        dispatch(actions.fetchEmployeeOptions());
    }, [dispatch]);

    // Auto-redirect to newly-created client
    useEffect(() => {
        if (lastCreatedClientId) {
            const id = lastCreatedClientId;
            dispatch(actions.clearLastCreatedClient());
            navigate(`/clients/${id}`);
        }
    }, [lastCreatedClientId, dispatch, navigate]);

    const handleSearch = useCallback(
        (newSearch) => {
            dispatch(actions.setFilters({ search: newSearch }));
            if (pagination.offset !== 1) dispatch(actions.setPagination({ offset: 1 }));
        },
        [dispatch, pagination.offset]
    );

    const handleTypeChange = (newValue) => {
        dispatch(actions.setFilters({ type: newValue ? newValue.value : '' }));
        if (pagination.offset !== 1) dispatch(actions.setPagination({ offset: 1 }));
    };
    const handleActiveChange = (newValue) => {
        dispatch(actions.setFilters({ isActive: newValue ? newValue.value : '' }));
        if (pagination.offset !== 1) dispatch(actions.setPagination({ offset: 1 }));
    };
    const handleEmployeeChange = (newValue) => {
        dispatch(actions.setFilters({ employeeEmail: newValue ? newValue.value : '' }));
        if (pagination.offset !== 1) dispatch(actions.setPagination({ offset: 1 }));
    };
    const handleTeamChange = (newValue) => {
        dispatch(actions.setFilters({ teamId: newValue ? newValue.value : '' }));
        if (pagination.offset !== 1) dispatch(actions.setPagination({ offset: 1 }));
    };
    const handlePageChange = (_, newPage) => dispatch(actions.setPagination({ offset: newPage }));
    const handleRowsPerPageChange = (value) =>
        dispatch(actions.setPagination({ limit: parseInt(value, 10), offset: 1 }));
    const handleSort = useCallback(
        (columnId) => {
            const same = sort.sortBy === columnId;
            dispatch(actions.setSort({ sortBy: columnId, sortDescending: same ? !sort.sortDescending : false }));
            if (pagination.offset !== 1) dispatch(actions.setPagination({ offset: 1 }));
        },
        [dispatch, sort.sortBy, sort.sortDescending, pagination.offset]
    );
    const handleCreate = () => dispatch(actions.openClientDialog());
    const handleClearFilters = () => {
        setSearchInput('');
        dispatch(actions.setFilters({ search: '', type: '', isActive: '', employeeEmail: '', teamId: '' }));
        dispatch(actions.setSort({ sortBy: 'updatedAt', sortDescending: true }));
        dispatch(actions.setPagination({ limit: 10, offset: 1 }));
    };

    // Build formatted options for employee and team filters
    const formattedEmployeeOptions = (employeeOptions || []).map((emp) => {
        const email = emp.email || emp.user?.email || emp.id;
        const name = [emp.firstname, emp.middlename, emp.lastname].filter(Boolean).join(' ') || email;
        return { value: email, label: name };
    });

    const formattedTeamOptions = (teamOptions || []).map((t) => ({ value: t.id, label: t.name }));

    // Derived selected option values (null when no filter active)
    const selectedTypeOption = filters.type
        ? CLIENT_TYPE_FILTER_OPTIONS.find((opt) => opt.value === filters.type) || null
        : null;
    const selectedActiveOption =
        filters.isActive !== '' && filters.isActive !== null && filters.isActive !== undefined
            ? ACTIVE_STATUS_FILTER_OPTIONS.find((opt) => String(opt.value) === String(filters.isActive)) || null
            : null;
    const selectedEmployeeOption = filters.employeeEmail
        ? formattedEmployeeOptions.find((opt) => opt.value === filters.employeeEmail) || null
        : null;
    const selectedTeamOption = filters.teamId
        ? formattedTeamOptions.find((opt) => opt.value === filters.teamId) || null
        : null;

    const filterConfigs = [
        {
            id: 'type',
            label: 'Type',
            options: CLIENT_TYPE_FILTER_OPTIONS,
            value: selectedTypeOption,
            onChange: handleTypeChange,
            minWidth: 140
        },
        {
            id: 'status',
            label: 'Status',
            options: ACTIVE_STATUS_FILTER_OPTIONS,
            value: selectedActiveOption,
            onChange: handleActiveChange,
            minWidth: 130
        },
        {
            id: 'employee',
            label: 'Employee',
            options: formattedEmployeeOptions,
            value: selectedEmployeeOption,
            onChange: handleEmployeeChange,
            minWidth: 170
        },
        {
            id: 'team',
            label: 'Team',
            options: formattedTeamOptions,
            value: selectedTeamOption,
            onChange: handleTeamChange,
            minWidth: 150
        }
    ];

    return (
        <CommonDataTable
            // Header
            title='Clients'
            searchName='client-search'
            searchPlaceholder='Search by name, industry, country...'
            searchValue={searchInput}
            onSearch={handleSearch}
            primaryAction={
                <Button
                    variant='contained'
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    size='small'
                    sx={{ height: '34px', minHeight: '34px' }}
                >
                    Create Client
                </Button>
            }
            // Filters
            filters={filterConfigs}
            onResetFilters={handleClearFilters}
            // Sorting
            sortField={sort.sortBy}
            sortDescending={sort.sortDescending}
            onSort={handleSort}
            // Data & columns
            columns={TABLE_COLUMNS}
            data={clients}
            totalRows={pagination.total}
            // Pagination
            page={pagination.offset}
            pageSize={pagination.limit}
            pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
            pageSizeName='clientManagement'
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            // Custom row rendering (ClientTableBody handles complex chips, menus, etc.)
            renderRow={(row, idx) => (
                <ClientTableBody key={row.id || idx} client={row} sort={sort} onSort={handleSort} />
            )}
            emptyState='No clients found.'
        >
            <ClientDialog />
            <DeleteConfirmationDialog />
        </CommonDataTable>
    );
};

export default ClientManagement;
